<script setup>
import {computed, ref} from "vue";
import TabContainer from "@/components/global-themed/tabs/TabContainer.vue";
import TabItem from "@/components/global-themed/tabs/TabItem.vue";
import TableRowRounded from "@/components/global-themed/table/TableRowRounded.vue";
import useApprovalSettings from "@/composables/approval-center/settings/useApprovalSettings";
import AutoApprovalConfirmationModal from "@/components/approval-center/settings/modals/AutoApprovalConfirmationModal.vue";
import {kitName} from "@/composables/useLocalDomains";

const behaviourOptions = [
  {label: "Auto publish", value: "auto_publish", tooltip: `${kitName.value} creates it and posts it with no approval.`},
  {label: "Needs approval", value: "needs_approval", tooltip: `${kitName.value} creates it and sends it to the Approval Center.`},
  {label: "Off", value: "off", tooltip: `${kitName.value} does not create this type of item.`},
];

const confirmationBehaviours = new Set(["auto_publish", "off"]);

const confirmationCopy = {
  auto_publish: {
    heading: "Confirm Automatic Publishing",
    text: "By enabling automatic publishing, all updates created for this item will be published immediately without entering the approval center. You’ll still be able to review everything in the change log, but you won’t approve changes before they go live.",
    subtext: "Are you sure you want to publish these updates automatically?",
    confirmLabel: "Enable auto publish",
    cancelLabel: "Cancel",
    state: "success",
  },
  off: {
    heading: "Turn Off Automated Updates?",
    text: "Turning this setting off means no further work will be completed for this item. Nothing will be created or queued in the approval center, and no updates will appear in the change log. This work will not be replaced by other tasks. You will still be able to complete these actions yourself using the platform.",
    subtext: "Are you sure you want to stop automated updates for this item?",
    confirmLabel: "Turn off",
    cancelLabel: "Cancel",
    state: "sad",
  },
};

const {settingsQuery, saveBehavioursMutation} = useApprovalSettings();
const {mutate: saveBehaviour, isPending} = saveBehavioursMutation;

const approvalTypes = computed(() => settingsQuery.data.value?.approval_behaviours || []);
const pendingSelection = ref(null);

const needsConfirmation = (behaviour) => confirmationBehaviours.has(behaviour);

const submitChange = (type, behaviour) => {
  if (!type || type.behaviour === behaviour || isPending.value) {
    return;
  }

  saveBehaviour(
    {
      behaviour,
      setting_name: type.name,
    },
  );
};

const handleSelect = (type, behaviour) => {
  if (needsConfirmation(behaviour)) {
    pendingSelection.value = {type, behaviour};
    return;
  }

  submitChange(type, behaviour);
};

const modalCopy = computed(() => {
  if (!pendingSelection.value) {
    return null;
  }

  return confirmationCopy[pendingSelection.value.behaviour];
});

const closeModal = () => {
  pendingSelection.value = null;
};

const confirmChange = () => {
  if (!pendingSelection.value) {
    return;
  }

  submitChange(pendingSelection.value.type, pendingSelection.value.behaviour);
  pendingSelection.value = null;
};
</script>

<template>
  <div class="tw-bg-white tw-rounded-lg tw-p-8 tw-flex tw-flex-col tw-gap-8">
    <div class="tw-flex tw-flex-col tw-gap-2">
      <p class="tw-font-bold tw-text-neutral-900 tw-text-2.5xl">
        Optimization Automation Settings
      </p>
      <p class="tw-text-neutral-900 tw-text-base tw-leading-snug">
        Choose how your website optimization is handled. You can publish updates automatically, send them to the approval center first, or turn off automation for items you prefer to manage manually.
      </p>
    </div>

    <div class="tw-flex tw-flex-col tw-gap-4">
      <TableRowRounded
        v-for="type in approvalTypes"
        :key="type.name"
        class="tw-grid-cols-12 tw-gap-6 tw-p-6"
      >
        <div class="tw-col-span-7 tw-flex tw-flex-col tw-gap-2">
          <p class="tw-text-lg tw-font-semibold tw-text-neutral-900">
            {{ type.display_name }}
          </p>
          <p class="tw-text-sm tw-text-neutral-600">
            {{ type.subtext }}
          </p>
        </div>

        <div class="tw-col-span-5 tw-flex tw-flex-col tw-gap-2">
          <TabContainer class="tw-grid tw-grid-cols-3 tw-gap-2">
            <TabItem
              v-for="option in behaviourOptions"
              :key="option.value"
              v-tooltip="{
                content: option.tooltip,
                placement: 'bottom',
                theme: 'dark-blue-tooltip',
              }"
              :active="type.behaviour === option.value"
              :class="[
                'tw-justify-center',
                {
                  'tw-pointer-events-none': isPending,
                }
              ]"
              @click="handleSelect(type, option.value)"
            >
              <span>{{ option.label }}</span>
            </TabItem>
          </TabContainer>
        </div>
      </TableRowRounded>
    </div>
  </div>

  <AutoApprovalConfirmationModal
    v-if="pendingSelection && modalCopy"
    :state="modalCopy.state"
    :confirm-label="modalCopy.confirmLabel"
    :cancel-label="modalCopy.cancelLabel"
    @close="closeModal"
    @confirm="confirmChange"
  >
    <template #heading>
      {{ modalCopy.heading }}
    </template>
    <template #text>
      {{ modalCopy.text }}
    </template>
    <template #subtext>
      {{ modalCopy.subtext }}
    </template>
  </AutoApprovalConfirmationModal>
</template>

